import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Alert,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Contractor } from '../../../types';
import api from '../../../services/api';

const AccountAccessControlTab: React.FC = () => {
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [loading, setLoading] = useState(true);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
  const [resetLink, setResetLink] = useState<string | null>(null);

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contractors');
      setContractors(response.data || []);
    } catch (error: any) {
      console.error('Error fetching contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (contractor: Contractor) => {
    try {
      const response = await api.post(`/contractors/${contractor.id}/reset-password`);
      setSelectedContractor(contractor);
      setResetLink(response.data.resetLink);
      setResetDialogOpen(true);
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleToggleAccess = async (contractor: Contractor) => {
    try {
      await api.put(`/contractors/${contractor.id}`, {
        isActive: !contractor.isActive,
      });
      fetchContractors();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update access');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Account & Access Control
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contractor Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Login Access</TableCell>
              <TableCell>Role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contractors.map((contractor) => (
              <TableRow key={contractor.id}>
                <TableCell>{contractor.name}</TableCell>
                <TableCell>{contractor.user?.email || contractor.contactEmail || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={contractor.isActive ? 'Active' : 'Inactive'}
                    color={contractor.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip label="CONTRACTOR" size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Reset Password">
                    <IconButton
                      size="small"
                      onClick={() => handleResetPassword(contractor)}
                    >
                      <LockResetIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={contractor.isActive ? 'Deactivate Access' : 'Activate Access'}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleAccess(contractor)}
                      color={contractor.isActive ? 'default' : 'success'}
                    >
                      {contractor.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Password Reset</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" gutterBottom>
              Contractor: {selectedContractor?.name}
            </Typography>
            {resetLink ? (
              <Box sx={{ mt: 2 }}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Password reset link generated successfully!
                </Alert>
                <TextField
                  label="Reset Link"
                  value={resetLink}
                  fullWidth
                  multiline
                  rows={2}
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  This link has been sent to {selectedContractor?.user?.email || selectedContractor?.contactEmail}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <TextField
                  label="Set New Password"
                  type="password"
                  fullWidth
                  helperText="Password must be at least 8 characters, contain one uppercase letter and one number"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Close</Button>
          {resetLink && (
            <Button
              onClick={() => {
                navigator.clipboard.writeText(resetLink);
                alert('Link copied to clipboard');
              }}
              variant="outlined"
            >
              Copy Link
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountAccessControlTab;

